import { Container, Graphics } from 'pixi.js';
import { OFFICE_DOORS } from '@duck/contracts';
import type { RoomContext, RoomDecorator } from './index';
import { place } from './index';
import { AO } from '../shading';
import { registerScenery, synthCtx } from '../scenery';

// ── EDITABLE SCENERY REGISTRATIONS (passive metadata for the level editor) ──────────────────────────
// Each bespoke kitchen FURNITURE piece is registered under a stable kind so the editor can re-render / add
// it. Builders take (ctx) and use the synthetic origin-anchored context; prep-island bakes its 80×52 top
// footprint (the exact literal args the place() call uses) into the kind. Painted/architectural backdrop
// (counter run, upper cabinets, menu board, sink, mug shelf, pot rack, pendant lamps) is NOT registered.
registerScenery('coffee-station', (art) => coffeeStation(synthCtx(art)), 'Coffee station', '☕');
registerScenery('fridge-tall', (art) => fridge(synthCtx(art)), 'Fridge', '🧊');
registerScenery('prep-island', (art) => prepIsland(synthCtx(art), 80, 52), 'Prep island', '🍳');
registerScenery('fruit-bowl', (art) => fruitBowl(synthCtx(art)), 'Fruit bowl', '🍎');
registerScenery('bar-stool', (art) => stool(synthCtx(art)), 'Bar stool', '🪑');
registerScenery('island-table', (art) => islandTable(synthCtx(art)), 'Island table', '🍽️');

/**
 * KITCHEN — the office galley / cafeteria, REDESIGNED for ORDER + STYLE (Floorplan v2, ADR-0022).
 *
 * The Creative Director flagged clutter (scattered plants, stray rug-blobs) and asked that every chair be
 * sittable. This pass tidies the room into three clean, legible zones and removes the scatter:
 *
 *   1. A single fitted COUNTER RUN hugging the NORTH wall — one continuous stone worktop on warm cabinetry
 *      with an under-mount sink + gooseneck faucet, the freestanding COFFEE STATION kept exactly on its
 *      interactive anchor (560,170), and a tall FRIDGE standing at the run's east end. Upper cabinets and
 *      a menu board fill the wall above; nothing breaks the clean horizontal line.
 *   2. A central PREP ISLAND that doubles as a breakfast bar — three BAR STOOLS perched at its SOUTH face
 *      (open floor), every one SITTABLE, all facing NORTH into the island. A copper pot rack hangs above;
 *      a fruit bowl sits on top.
 *   3. A small DINING NOOK on the right — a round café table with two CHAIRS facing each other across it,
 *      both SITTABLE — the room's sit-down corner, clear of the south + east door lanes.
 *
 * ORDER: counter lines stay flush + aligned; stools/chairs are squared to their tables. STYLE: warm wood +
 * pale stone, copper accents, an appetizing palette — no clutter. PLANTS: exactly TWO, one per south corner
 * (deliberate framing of the entries), never scattered. RUG: NONE — a kitchen reads cleaner with bare floor,
 * and it kills the stray yellow rug-blobs entirely. Themed off `art` (re-skins per world) and built ONCE on
 * map build/re-skin — no animation, no per-frame allocation.
 *
 * SITTABLE SEATS (report to the seats contract; facing in radians, -π/2 = north, π/2 = south, 0 = east,
 * π = west — the duck SNAPS to the anchor and faces that way):
 *   - kitchen-bar-1 : (602,210) facing -π/2  (bar stool, faces N into the prep island)
 *   - kitchen-bar-2 : (632,210) facing -π/2  (bar stool)
 *   - kitchen-bar-3 : (662,210) facing -π/2  (bar stool)
 *   - kitchen-dine-1: (922,176) facing 0     (dining chair, faces E across the table)
 *   - kitchen-dine-2: (1018,176) facing π    (dining chair, faces W across the table)
 *
 * Geometry (zone x[440..1120] y[40..280], centre 780,160) — WIDE. THREE doors, all kept clear:
 *   - kitchen-W on the WEST edge → opening y[90..230] (to the terrace);
 *   - kitchen-E on the EAST edge → opening y[90..230] (to the meeting room);
 *   - kitchen-S on the SOUTH edge → opening x[680..880] (to the open-space hub).
 * The counter rides the north wall (y≈52..80), north of the side openings, so it never intrudes on the W/E
 * lanes. The prep island + its bar stools sit LEFT of the south lane (their east edge ≈672, a hair west of
 * x680); the dining nook sits east of the lane (x≈922+). A ≥40px margin in front of every doorway and the
 * full south corridor (x[680..880]) stay open — a duck threads in from any side without a snag.
 */

/** A tall office fridge with a freezer-over-fridge split, handle bars and a little magnet — inline (kitchen-only). */
function fridge(ctx: RoomContext): Container {
  const { art, mix, lighten, darken } = ctx;
  const c = new Container();
  const body = mix(0xcdd3da, art.wall, 0.25); // brushed-steel-ish, tinted to theme
  c.addChild(new Graphics().ellipse(0, 40, 20, 7).fill({ color: 0x000000, alpha: 0.22 }));
  const g = new Graphics()
    // body
    .roundRect(-18, -44, 36, 84, 5)
    .fill(body)
    // soft left highlight + right shade so it reads as a rounded metal box
    .roundRect(-18, -44, 8, 84, 5)
    .fill({ color: 0xffffff, alpha: 0.1 })
    .roundRect(11, -44, 7, 84, 5)
    .fill({ color: 0x000000, alpha: 0.08 })
    // bottom ambient-occlusion band (away from the top-left key light)
    .roundRect(-18, 28, 36, 12, 5)
    .fill({ color: 0x000000, alpha: AO * 0.5 })
    // freezer/fridge split seam
    .rect(-18, -10, 36, 2)
    .fill(darken(body, 0.25))
    // vertical handles
    .roundRect(8, -36, 3, 18, 1.5)
    .fill(darken(body, 0.35))
    .roundRect(8, -2, 3, 32, 1.5)
    .fill(darken(body, 0.35))
    // a tiny fridge magnet for character
    .circle(-8, -24, 2.5)
    .fill({ color: art.accent, alpha: 0.9 });
  c.addChild(g);
  // a fresh top highlight strip
  c.addChild(new Graphics().roundRect(-16, -43, 32, 3, 2).fill({ color: lighten(body, 0.18), alpha: 0.8 }));
  return c;
}

/**
 * The back COUNTER RUN: a continuous stone worktop on warm base cabinets that spans the given x range,
 * with cabinet-door seams + pulls. Built as one inline piece so the run reads as a single fitted kitchen
 * (rather than separate `f.cabinet` boxes with gaps). Origin at its left end; surface top at local y=0.
 */
function counterRun(ctx: RoomContext, w: number): Container {
  const { art, mix, lighten, darken } = ctx;
  const c = new Container();
  const cab = mix(0x7a5733, art.wall, 0.22); // warm wood cabinetry
  const top = mix(0xe7e3da, art.wall, 0.12); // pale stone worktop
  c.addChild(new Graphics().rect(2, 50, w - 4, 6).fill({ color: 0x000000, alpha: 0.16 }));
  const g = new Graphics()
    // base cabinets
    .roundRect(0, 8, w, 44, 3)
    .fill(cab)
    .rect(0, 8, w, 4)
    .fill({ color: 0x000000, alpha: 0.1 })
    // bottom ambient-occlusion band + a kick-board shadow at the floor (away from the key light)
    .rect(0, 45, w, 7)
    .fill({ color: 0x000000, alpha: AO * 0.45 })
    .rect(0, 50, w, 2)
    .fill(darken(cab, 0.3))
    // stone worktop overhanging slightly, with a lit front lip
    .roundRect(-3, -2, w + 6, 12, 2)
    .fill(top)
    .roundRect(-3, -2, w + 6, 3, 2)
    .fill({ color: 0xffffff, alpha: 0.22 });
  // cabinet-door faces with pulls, repeated across the run
  const door = 44;
  for (let dx = 6; dx + door <= w - 6; dx += door + 4) {
    g.roundRect(dx, 14, door, 32, 2)
      .fill(darken(cab, 0.1))
      .roundRect(dx + door - 8, 26, 3, 8, 1.5)
      .fill(lighten(art.accent, 0.1));
  }
  c.addChild(g);
  return c;
}

/** An under-mount sink basin + a gooseneck faucet, drawn on top of the worktop at local origin. */
function sink(ctx: RoomContext): Container {
  const { art, mix, darken } = ctx;
  const c = new Container();
  const steel = mix(0x9aa1ab, art.wall, 0.15);
  c.addChild(
    new Graphics()
      // basin recess in the worktop
      .roundRect(-17, -1, 34, 9, 3)
      .fill(darken(steel, 0.32))
      .roundRect(-14, 0, 28, 6, 2)
      .fill(darken(steel, 0.12))
      // a faint water sheen
      .roundRect(-12, 1, 24, 2, 1)
      .fill({ color: 0xbfe6ff, alpha: 0.35 }),
  );
  // gooseneck faucet rising behind the basin
  c.addChild(
    new Graphics()
      .moveTo(13, -2)
      .lineTo(13, -16)
      .bezierCurveTo(13, -22, 2, -22, 2, -16)
      .stroke({ color: steel, width: 3, alpha: 0.95 })
      .circle(2, -15, 1.6)
      .fill(darken(steel, 0.2)),
  );
  return c;
}

/**
 * The freestanding COFFEE STATION (the interactive coffee-machine's anchor): a little warm-wood coffee bar
 * with a stone top, the drip machine + carafe on top, a brew light, and a couple of cups beside it. Origin
 * centred on the cabinet so it lands exactly on the prop anchor; body rises "up" the screen off it.
 */
function coffeeStation(ctx: RoomContext): Container {
  const { art, mix, lighten, darken } = ctx;
  const c = new Container();
  const cab = mix(0x7a5733, art.wall, 0.22); // warm wood, matching the counter run
  const top = mix(0xe7e3da, art.wall, 0.12); // pale stone top
  const body = darken(0xf0f1f3, 0.06); // appliance plastic
  // contact shadow under the cabinet
  c.addChild(new Graphics().ellipse(0, 16, 26, 8).fill({ color: 0x000000, alpha: 0.2 }));
  c.addChild(
    new Graphics()
      // base cabinet body
      .roundRect(-24, -8, 48, 26, 3)
      .fill(cab)
      .roundRect(-24, -8, 48, 4, 3)
      .fill({ color: 0xffffff, alpha: 0.12 })
      // kick-board shadow at the floor
      .rect(-24, 16, 48, 2)
      .fill(darken(cab, 0.3))
      // two little doors with pulls
      .roundRect(-21, -3, 18, 18, 2)
      .fill(darken(cab, 0.1))
      .roundRect(3, -3, 18, 18, 2)
      .fill(darken(cab, 0.1))
      .circle(-6, 6, 1.6)
      .fill(lighten(art.accent, 0.1))
      .circle(6, 6, 1.6)
      .fill(lighten(art.accent, 0.1))
      // bottom ambient-occlusion band (away from the top-left key light)
      .rect(-22, 11, 44, 6)
      .fill({ color: 0x000000, alpha: AO * 0.4 })
      // stone top overhanging, with a lit front lip
      .roundRect(-27, -14, 54, 9, 2)
      .fill(top)
      .roundRect(-27, -14, 54, 2.5, 2)
      .fill({ color: 0xffffff, alpha: 0.22 }),
  );
  // the drip coffee machine sitting on top (slightly left of centre)
  c.addChild(
    new Graphics()
      // reservoir/back body
      .roundRect(-17, -34, 18, 18, 3)
      .fill(body)
      .roundRect(-17, -34, 18, 4, 3)
      .fill({ color: 0xffffff, alpha: 0.25 })
      // brew-ready light
      .circle(-3, -20, 1.4)
      .fill({ color: art.glow, alpha: 0.95 })
      // hot plate + glass carafe
      .roundRect(-16, -15, 16, 3, 1)
      .fill(darken(body, 0.4))
      .roundRect(-14, -24, 12, 10, 2)
      .fill({ color: lighten(0x5a3a22, 0.1), alpha: 0.55 })
      .roundRect(-14, -19, 12, 5, 1)
      .fill({ color: 0x5a3a22, alpha: 0.75 }),
  );
  // a couple of stacked cups beside it for the lived-in look
  c.addChild(
    new Graphics()
      .roundRect(8, -20, 8, 6, 2)
      .fill({ color: lighten(art.accent, 0.2), alpha: 0.95 })
      .roundRect(8, -20, 8, 2, 2)
      .fill({ color: 0xffffff, alpha: 0.25 })
      .roundRect(9, -25, 6, 5, 2)
      .fill({ color: 0x6cc7e0, alpha: 0.95 }),
  );
  return c;
}

/** Upper wall cabinets above the counter: a fitted row of doors with pulls, mounted high on the back wall. */
function upperCabinets(ctx: RoomContext, w: number): Container {
  const { art, mix, lighten, darken } = ctx;
  const c = new Container();
  const cab = mix(0x8a6740, art.wall, 0.22);
  const g = new Graphics()
    .roundRect(0, 0, w, 26, 3)
    .fill(cab)
    .rect(0, 0, w, 3)
    .fill({ color: 0xffffff, alpha: 0.14 })
    // shadow the cabinets cast onto the wall below
    .rect(0, 26, w, 3)
    .fill({ color: 0x000000, alpha: 0.1 });
  const door = 40;
  for (let dx = 5; dx + door <= w - 5; dx += door + 4) {
    g.roundRect(dx, 4, door, 18, 2)
      .fill(darken(cab, 0.08))
      .circle(dx + door - 6, 13, 1.6)
      .fill(lighten(art.accent, 0.1));
  }
  c.addChild(g);
  return c;
}

/** An open wall shelf bearing a row of colourful mugs — the homey touch over the coffee station. */
function mugShelf(ctx: RoomContext, w: number): Container {
  const { art, mix, darken } = ctx;
  const c = new Container();
  const plank = mix(0x6a4a2b, art.wall, 0.3);
  const g = new Graphics()
    .roundRect(0, 8, w, 4, 1.5)
    .fill(plank)
    .rect(0, 12, w, 2)
    .fill(darken(plank, 0.35));
  // a little row of mugs with handles
  const mugs = [mix(art.accent, 0xffffff, 0.25), 0xff8a6b, 0x6cc7e0, 0xf4d35e, 0x9bd17a];
  for (let i = 0; i < Math.min(mugs.length, Math.floor((w - 6) / 14)); i++) {
    const mx = 8 + i * 14;
    g.roundRect(mx - 4, -1, 8, 9, 2)
      .fill(mugs[i]!)
      .roundRect(mx - 4, -1, 8, 2, 2)
      .fill({ color: 0xffffff, alpha: 0.2 })
      // handle
      .circle(mx + 5, 3, 2.4)
      .stroke({ color: mugs[i]!, width: 1.6 });
  }
  c.addChild(g);
  return c;
}

/**
 * A wall-mounted MENU / SPECIALS board: a dark slate panel in a warm frame with a couple of chalk lines
 * and a tiny doodle — the cafeteria's bit of personality. Origin centred; hangs flat on the wall.
 */
function menuBoard(ctx: RoomContext): Container {
  const { art, mix, lighten, darken } = ctx;
  const c = new Container();
  const frame = mix(0x6a4a2b, art.wall, 0.3);
  const slate = darken(mix(0x2a3a30, art.wall, 0.12), 0.1);
  c.addChild(
    new Graphics()
      // frame + slate
      .roundRect(-30, -18, 60, 36, 3)
      .fill(frame)
      .roundRect(-30, -18, 60, 4, 3)
      .fill({ color: 0xffffff, alpha: 0.1 })
      .roundRect(-25, -13, 50, 26, 2)
      .fill(slate)
      // a little "COFFEE ✓" header underline in accent chalk
      .moveTo(-19, -6)
      .lineTo(8, -6)
      .stroke({ color: lighten(art.accent, 0.25), width: 2, alpha: 0.85 })
      // two faint chalk menu lines
      .moveTo(-19, 1)
      .lineTo(15, 1)
      .moveTo(-19, 7)
      .lineTo(4, 7)
      .stroke({ color: 0xf2efe6, width: 1.5, alpha: 0.6 })
      // a tiny steaming-cup doodle in the corner
      .roundRect(13, 2, 8, 7, 2)
      .stroke({ color: 0xf2efe6, width: 1.4, alpha: 0.7 }),
  );
  return c;
}

/**
 * A PENDANT LAMP hanging over a counter/island: a cord dropping from the ceiling to a warm dome shade that
 * casts a soft glow pool on the surface below. Origin at the shade centre; the glow falls "down" (+y).
 */
function pendantLamp(ctx: RoomContext): Container {
  const { art, mix, darken } = ctx;
  const c = new Container();
  const shade = mix(0xf4e8c8, art.accent, 0.18);
  // soft glow pool below the lamp
  c.addChild(new Graphics().ellipse(0, 16, 20, 9).fill({ color: art.glow, alpha: 0.1 * art.glowStrength + 0.05 }));
  c.addChild(
    new Graphics()
      // cord up to the ceiling
      .rect(-1, -22, 2, 18)
      .fill(darken(art.wall, 0.3))
      // dome shade
      .moveTo(-11, -4)
      .bezierCurveTo(-11, -16, 11, -16, 11, -4)
      .lineTo(-11, -4)
      .fill(shade)
      // lit underside rim
      .roundRect(-11, -5, 22, 3, 1.5)
      .fill({ color: art.glow, alpha: 0.85 })
      // top highlight
      .moveTo(-7, -12)
      .bezierCurveTo(-7, -15, 7, -15, 7, -12)
      .stroke({ color: 0xffffff, width: 1.5, alpha: 0.35 }),
  );
  return c;
}

/**
 * A round DINING / coffee ISLAND table — a stone-topped pedestal table that anchors the cafeteria seating.
 * Slightly larger and a touch more "café" than the catalog coffeeTable, with a central pedestal so stools
 * can tuck close all around. Origin centred; a soft top sheen reads as a wiped surface.
 */
function islandTable(ctx: RoomContext): Container {
  const { art, mix, lighten, darken } = ctx;
  const c = new Container();
  const top = mix(0xe7e3da, art.wall, 0.12); // pale stone, matching the worktops
  const ped = mix(0x6a4a2b, art.wall, 0.3); // warm wood pedestal
  c.addChild(new Graphics().ellipse(0, 12, 36, 13).fill({ color: 0x000000, alpha: 0.22 }));
  c.addChild(
    new Graphics()
      // pedestal foot + column
      .ellipse(0, 14, 12, 5)
      .fill(darken(ped, 0.2))
      .rect(-4, -2, 8, 16)
      .fill(ped)
      // round stone top
      .ellipse(0, 6, 34, 15)
      .fill(darken(top, 0.16))
      .ellipse(0, 0, 34, 16)
      .fill(top)
      .ellipse(0, -3, 26, 11)
      .fill({ color: 0xffffff, alpha: 0.12 }),
  );
  // a tiny napkin holder + a mug on the table, for life
  c.addChild(
    new Graphics()
      .roundRect(-3, -6, 8, 5, 1.5)
      .fill({ color: lighten(art.accent, 0.2), alpha: 0.9 })
      .roundRect(-14, -4, 7, 6, 2)
      .fill({ color: 0xff8a6b, alpha: 0.95 })
      .circle(-3, -8, 1.6)
      .fill({ color: art.glow, alpha: 0.6 }),
  );
  return c;
}

/** A simple round bar STOOL (no back) for tucking around the island — seat + a small contact shadow. */
function stool(ctx: RoomContext): Container {
  const { art, lighten, darken } = ctx;
  const c = new Container();
  const seat = darken(art.wall, 0.16);
  c.addChild(new Graphics().ellipse(0, 6, 10, 4).fill({ color: 0x000000, alpha: 0.2 }));
  c.addChild(
    new Graphics()
      // little leg shadow
      .rect(-2, 0, 4, 7)
      .fill(darken(seat, 0.25))
      // round padded seat
      .ellipse(0, -1, 11, 7)
      .fill(seat)
      .ellipse(0, -3, 9, 5)
      .fill(lighten(seat, 0.12)),
  );
  return c;
}

/**
 * The central PREP ISLAND: a freestanding rectangular butcher-block-on-stone island for the empty
 * centre-left floor. A warm-wood cabinet base carries a pale stone top (with a wiped sheen) and a chopping
 * board + knife block on the surface (the caller adds the stools + pot rack + fruit bowl around it). Origin
 * centred on the top; `topW`×`topH` give the worktop footprint so the caller can keep it clear of the lane.
 */
function prepIsland(ctx: RoomContext, topW: number, topH: number): Container {
  const { art, mix, lighten, darken } = ctx;
  const c = new Container();
  const cab = mix(0x7a5733, art.wall, 0.22); // warm wood, matching the counter run
  const top = mix(0xe7e3da, art.wall, 0.12); // pale stone top
  const block = mix(0xb98a55, art.wall, 0.18); // butcher-block insert
  const hw = topW / 2;
  const hh = topH / 2;
  // grounding contact shadow under the whole island
  c.addChild(new Graphics().ellipse(0, hh + 4, hw + 6, hh * 0.7 + 6).fill({ color: 0x000000, alpha: 0.2 }));
  const g = new Graphics()
    // base cabinet body, dropping below the top
    .roundRect(-hw + 4, -hh + 6, topW - 8, topH + 14, 4)
    .fill(cab)
    .roundRect(-hw + 4, -hh + 6, topW - 8, 4, 4)
    .fill({ color: 0xffffff, alpha: 0.1 })
    // kick-board shadow at the floor
    .rect(-hw + 4, hh + 16, topW - 8, 3)
    .fill(darken(cab, 0.3));
  // cabinet-door faces with little pulls across the visible (south) face
  const door = 40;
  for (let dx = -hw + 10; dx + door <= hw - 10; dx += door + 6) {
    g.roundRect(dx, -hh + 12, door, topH + 2, 2)
      .fill(darken(cab, 0.1))
      .circle(dx + door - 6, -hh + 12 + (topH + 2) / 2, 1.6)
      .fill(lighten(art.accent, 0.1));
  }
  // pale stone worktop, overhanging the cabinet with a lit front lip
  g.roundRect(-hw, -hh, topW, topH, 3)
    .fill(top)
    .roundRect(-hw, -hh, topW, 4, 3)
    .fill({ color: 0xffffff, alpha: 0.2 })
    // a soft wiped sheen across the surface
    .roundRect(-hw + 8, -hh + 6, topW - 16, topH - 14, 4)
    .fill({ color: 0xffffff, alpha: 0.07 });
  c.addChild(g);
  // a chopping board + knife block + a tomato, for the prepped-in look
  c.addChild(
    new Graphics()
      // wooden chopping board
      .ellipse(-hw * 0.42, 0, 16, 9)
      .fill(block)
      .ellipse(-hw * 0.42, -2, 13, 7)
      .fill(lighten(block, 0.1))
      // a tomato resting on it
      .circle(-hw * 0.42, -1, 3)
      .fill({ color: 0xe2533a, alpha: 0.95 })
      .circle(-hw * 0.42 - 1, -2.5, 1)
      .fill({ color: 0xffffff, alpha: 0.3 })
      // a little knife block to the right
      .roundRect(hw * 0.32 - 7, -7, 14, 12, 2)
      .fill(darken(cab, 0.15))
      .rect(hw * 0.32 - 3, -12, 1.6, 6)
      .fill(darken(art.wall, 0.4))
      .rect(hw * 0.32 + 1, -11, 1.6, 5)
      .fill(darken(art.wall, 0.4)),
  );
  return c;
}

/**
 * A hanging POT RACK over the prep island: a wood rail suspended from two cords with a row of copper pots
 * and a pan dangling beneath it. Origin at the rail centre; pots hang "down" (+y) toward the island top.
 */
function potRack(ctx: RoomContext, w: number): Container {
  const { art, mix, lighten, darken } = ctx;
  const c = new Container();
  const rail = mix(0x6a4a2b, art.wall, 0.3);
  const copper = mix(0xc77b40, art.accent, 0.12);
  const hw = w / 2;
  const g = new Graphics()
    // two cords up to the ceiling
    .rect(-hw + 8, -20, 2, 18)
    .fill(darken(art.wall, 0.3))
    .rect(hw - 10, -20, 2, 18)
    .fill(darken(art.wall, 0.3))
    // the wood rail
    .roundRect(-hw, -3, w, 6, 2)
    .fill(rail)
    .roundRect(-hw, -3, w, 2, 2)
    .fill({ color: 0xffffff, alpha: 0.12 });
  c.addChild(g);
  // a row of hanging copper pots/pans of varied size
  const pots = [
    { dx: -hw + 14, r: 7 },
    { dx: -hw + 32, r: 9 },
    { dx: 2, r: 8 },
    { dx: hw - 30, r: 9 },
    { dx: hw - 12, r: 6 },
  ];
  const p = new Graphics();
  for (const { dx, r } of pots) {
    p.moveTo(dx, 2)
      .lineTo(dx, 6)
      .stroke({ color: darken(rail, 0.2), width: 1.4 }) // little hook
      .roundRect(dx - r, 6, r * 2, r, 2)
      .fill(copper)
      .roundRect(dx - r, 6, r * 2, 2.5, 2)
      .fill({ color: lighten(copper, 0.25), alpha: 0.85 }); // rim highlight
  }
  c.addChild(p);
  return c;
}

/** A FRUIT BOWL — a shallow ceramic bowl with a few rounded fruits, a small homey touch for the island. */
function fruitBowl(ctx: RoomContext): Container {
  const { art, mix, lighten, darken } = ctx;
  const c = new Container();
  const bowl = mix(0xe9e4dc, art.accent, 0.1);
  c.addChild(new Graphics().ellipse(0, 4, 15, 6).fill({ color: 0x000000, alpha: 0.16 }));
  c.addChild(
    new Graphics()
      // fruit poking above the rim
      .circle(-5, -4, 4)
      .fill({ color: 0xe2533a, alpha: 0.95 }) // apple
      .circle(4, -5, 4)
      .fill({ color: 0xf4b13a, alpha: 0.95 }) // orange
      .circle(0, -3, 3.5)
      .fill({ color: 0x9bd17a, alpha: 0.95 }) // green apple
      .circle(-5, -5, 1.2)
      .fill({ color: 0xffffff, alpha: 0.35 })
      // the shallow bowl
      .ellipse(0, 0, 14, 6)
      .fill(darken(bowl, 0.12))
      .ellipse(0, -1, 13, 5)
      .fill(bowl)
      .ellipse(0, -2, 10, 3)
      .fill(lighten(bowl, 0.12)),
  );
  return c;
}

const decorate: RoomDecorator = (ctx: RoomContext) => {
  const { zone, art, f } = ctx;
  const x0 = zone.x; // 440
  const y0 = zone.y; // 40
  const right = zone.x + zone.width; // 1120
  const bottom = zone.y + zone.height; // 280

  // Doors (for clearance reasoning): W & E side lanes y[90..230]; S lane x[680..880]. We keep ≥40px in
  // front of each: the counter rides the north wall (y≈52..80, north of the side openings), so it never
  // intrudes on the W/E lanes; the prep island (left) and the dining nook (right) bracket the open south
  // corridor and never spill into it.
  const doorS = OFFICE_DOORS.find((d) => d.zoneId === zone.id && d.side === 'S');
  void doorS;

  // ── 1. BACK COUNTER RUN (north wall) — one clean, continuous line. ───────────────────────────────
  // A single fitted run across the middle of the north wall, started a comfortable margin in from the
  // WEST door lane and ended before the fridge nook on the east. Surface line sits just under the top band
  // so the whole north edge reads as one tidy worktop — no breaks, no clutter on the wall.
  const runX = x0 + 78; // 518 — clear of the west-door lane (x≈440..480)
  const runW = 470; // ends ~988, leaving the back-right corner for the fridge
  const counterTopY = y0 + 22; // 62 — worktop surface line, high on the north wall
  place(ctx, counterRun(ctx, runW), runX, counterTopY);

  // Upper cabinets mounted high on the back wall above the run, in two even banks flanking the menu board.
  place(ctx, upperCabinets(ctx, 168), runX + 6, y0 + 2);
  place(ctx, upperCabinets(ctx, 168), runX + 296, y0 + 2);
  // A menu / specials board centred over the run for cafeteria personality.
  place(ctx, menuBoard(ctx), runX + 240, y0 + 18);

  // The under-mount sink sits ON the worktop, aligned east of the coffee station so the two read as a
  // squared-off prep + wash zone (sink at ≈668 — between the coffee bar and the fridge).
  place(ctx, sink(ctx), runX + 150, counterTopY);

  // ── COFFEE STATION — the interactive prop anchor at (560,170), seated INTO the north counter line. ─
  // Drawn as the coffee bar exactly on its anchor so the "use coffee machine" action lines up; a mug shelf
  // + a pendant lamp hang above it on the north wall, keeping the station part of the tidy counter run.
  place(ctx, coffeeStation(ctx), 560, 170, 'coffee-station');
  place(ctx, mugShelf(ctx, 76), 524, y0 + 50);
  place(ctx, pendantLamp(ctx), 560, y0 + 60);

  // ── FRIDGE (north wall, east end of the counter run). ────────────────────────────────────────────
  // Stands as a floor appliance at the run's east end. Body (local x[-18..18], y[-44..40]) at (1022,110)
  // spans x[1004..1040], y[66..150] — top tucked at the back-wall line, a clear ~80px gap to the east-door
  // lane (x≈1120, y[90..230]), and well north of the dining nook below.
  place(ctx, fridge(ctx), runX + runW + 34, y0 + 70, 'fridge-tall');

  // ── 2. CENTRAL PREP ISLAND + 3 BAR STOOLS (all sittable) — biased LEFT of the south lane. ─────────
  // Top footprint 80×52 centred at (632,158) spans x[592..672] × y[132..184]: nestled just east of the
  // coffee station (its east edge ≈587) and its own east edge stopping at x672 — a hair west of the south
  // lane (x680) — so the south corridor stays a clean walk-through. It doubles as a breakfast bar: three
  // bar stools perch at its SOUTH face (open floor), ALL sittable, all facing NORTH into the island, none
  // on the counter (N), lane (E) or coffee-bar (W) sides. A copper pot rack hangs above; a fruit bowl on top.
  const px = 632;
  const py = 158;
  place(ctx, prepIsland(ctx, 80, 52), px, py, 'prep-island');
  place(ctx, potRack(ctx, 78), px, py - 80); // hangs on the wall above (≈632,78)
  place(ctx, fruitBowl(ctx), px + 20, py - 12, 'fruit-bowl'); // fruit bowl on the worktop's east end
  // The three SITTABLE bar stools — squared along the island's south face, evenly 30px apart, anchors at
  // (602,210) (632,210) (662,210); each is well west of the south-door lane (x680). Seats face NORTH.
  place(ctx, stool(ctx), px - 30, py + 52, 'bar-stool'); // kitchen-bar-1 → anchor (602,210)
  place(ctx, stool(ctx), px, py + 52, 'bar-stool'); // kitchen-bar-2 → anchor (632,210)
  place(ctx, stool(ctx), px + 30, py + 52, 'bar-stool'); // kitchen-bar-3 → anchor (662,210)

  // ── 3. DINING NOOK + 2 CHAIRS (all sittable) — right of the south lane, clear of the east lane. ───
  // A round café table centred at (970,176): well east of the south-door lane (x[680..880]) and clear of
  // the east-door lane (table east edge ≈1004, lane at x≈1120). Two SITTABLE chairs face each other across
  // it (a real sit-down corner), squared W↔E. A pendant lamp hangs over the table. This furnishes the wide
  // room's right half without scatter.
  const tx = 970;
  const ty = 176;
  place(ctx, f.deskChair(art, 'down'), tx - 48, ty); // kitchen-dine-1 → anchor (922,176), faces E (0)
  place(ctx, f.deskChair(art, 'down'), tx + 48, ty); // kitchen-dine-2 → anchor (1018,176), faces W (π)
  place(ctx, islandTable(ctx), tx, ty, 'island-table');
  place(ctx, pendantLamp(ctx), tx, y0 + 64);

  // ── GREENERY — exactly TWO plants, one per SOUTH corner, deliberate (never scattered). ────────────
  // Both south of the side-door lanes (which end at y=230), framing the entries without pinching them.
  // SW corner: a tall plant tucked beside the west entry. SE corner: a leafy plant by the dining nook.
  place(ctx, f.plant(art, 'tall'), x0 + 26, bottom - 30); // ≈466,250
  place(ctx, f.plant(art, 'leafy'), right - 28, bottom - 32); // ≈1092,248
};

export default decorate;
